import React, { Component, type ReactNode, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, MemoryRouter } from 'react-router';
import { Layout } from './components/common/Layout';
import { MiniAppDisabledScreen } from './components/common/MiniAppDisabledScreen';
import { PageSkeleton } from './components/common/PageSkeleton';
import { useAppSettings } from './stores/appSettings';
import { safeLocalStorage } from './utils/storage';
import { logMiniAppEvent } from './utils/api';

import { getPlatformBridge } from './services/platform-bridge';

// Хелпер с автоматической перезагрузкой при обновлении версий бандлов на сервере
function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error: any) {
      console.warn('[lazyWithRetry] Chunk load failure, attempting auto-reload...', error);
      const isChunkError =
        error?.message?.includes('Failed to fetch dynamically imported module') ||
        error?.message?.includes('Importing a module script failed') ||
        error?.message?.includes('error loading dynamically imported module');
      if (isChunkError && typeof window !== 'undefined') {
        const reloadKey = 'chunk_reload_' + window.location.pathname;
        if (!sessionStorage.getItem(reloadKey)) {
          sessionStorage.setItem(reloadKey, '1');
          window.location.reload();
          return new Promise<{ default: T }>(() => {});
        }
      }
      throw error;
    }
  });
}

// Ленивая загрузка страниц для уменьшения начального размера бандла
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const BookingsPage = lazyWithRetry(() => import('./pages/BookingsPage').then(m => ({ default: m.BookingsPage })));
const SettingsPage = lazyWithRetry(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const StaffPage = lazyWithRetry(() => import('./pages/StaffPage').then(m => ({ default: m.StaffPage })));
const OwnersPage = lazyWithRetry(() => import('./pages/OwnersPage').then(m => ({ default: m.OwnersPage })));
const LogsPage = lazyWithRetry(() => import('./pages/LogsPage').then(m => ({ default: m.LogsPage })));
const BillingPage = lazyWithRetry(() => import('./pages/BillingPage').then(m => ({ default: m.BillingPage })));
const AdminAIPage = lazyWithRetry(() => import('./pages/AdminAIPage').then(m => ({ default: m.AdminAIPage })));
const ReviewsPage = lazyWithRetry(() => import('./pages/ReviewsPage').then(m => ({ default: m.ReviewsPage })));
const PromoPage = lazyWithRetry(() => import('./pages/PromoPage').then(m => ({ default: m.PromoPage })));
const FinancePage = lazyWithRetry(() => import('./pages/FinancePage').then(m => ({ default: m.FinancePage })));
const CRMPage = lazyWithRetry(() => import('./pages/CRMPage').then(m => ({ default: m.CRMPage })));
const ClientBookingPage = lazyWithRetry(() => import('./pages/ClientBookingPage').then(m => ({ default: m.ClientBookingPage })));
const ClientHistoryPage = lazyWithRetry(() => import('./pages/ClientHistoryPage').then(m => ({ default: m.ClientHistoryPage })));
const ClientLoyaltyPage = lazyWithRetry(() => import('./pages/ClientLoyaltyPage').then(m => ({ default: m.ClientLoyaltyPage })));
const OnboardingPage = lazyWithRetry(() => import('./pages/OnboardingPage').then(m => ({ default: m.OnboardingPage })));
const AcceptInvitePage = lazyWithRetry(() => import('./pages/AcceptInvitePage').then(m => ({ default: m.AcceptInvitePage })));
const SandboxPage = lazyWithRetry(() => import('./pages/SandboxPage').then(m => ({ default: m.SandboxPage })));

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
    try {
      const bridge = getPlatformBridge();
      const initData = bridge.initData;
      if (initData) {
        logMiniAppEvent(
          initData,
          'error',
          `[React UI Crash] ${error.message}`,
          `Stack: ${error.stack || ''}\nComponentStack: ${errorInfo.componentStack || ''}`
        ).catch(() => {});
      }
      bridge.ready();
    } catch {
      /* ignore */
    }
  }

  public override render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: 'var(--tg-viewport-height, 100dvh)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          textAlign: 'center',
          background: 'var(--tg-theme-bg-color, #ffffff)',
          color: 'var(--tg-theme-text-color, #000000)',
          fontFamily: 'system-ui, sans-serif'
        }}>
          <span style={{ fontSize: '64px', marginBottom: '16px' }}>⚠️</span>
          <h2 style={{ fontSize: '20px', fontWeight: 800, marginBottom: '8px' }}>Что-то пошло не так</h2>
          <p style={{ fontSize: '13px', opacity: 0.6, marginBottom: '24px', maxWidth: '300px', lineHeight: 1.5 }}>
            Произошла непредвиденная ошибка в приложении. Мы уже работаем над её устранением.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: 'var(--tg-theme-button-color, #007AFF)',
              color: 'var(--tg-theme-button-text-color, #ffffff)',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Перезагрузить приложение 🔄
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

function HomeWrapper(): React.JSX.Element {
  const { isClientMode } = useAppSettings();
  return (
    <Suspense fallback={<PageSkeleton />}>
      {isClientMode ? <ClientBookingPage /> : <DashboardPage />}
    </Suspense>
  );
}

function SafeBrowserRouter({ children }: { children: React.ReactNode }) {
  const initialEntries = React.useMemo(() => {
    if (typeof window === 'undefined') return ['/'];
    let path = window.location.pathname;
    if (path.startsWith('/app')) {
      path = path.slice(4) || '/';
    }
    if (!path.startsWith('/')) path = '/' + path;
    const fullPath = path + window.location.search + window.location.hash;
    return [fullPath || '/'];
  }, []);

  // В мессенджерах (Telegram, MAX) и мобильных WebViews используем MemoryRouter:
  // это полностью изолирует роутинг от ограничений нативного WebKit и проблем с basename="/app"
  const isWebView = typeof window !== 'undefined' && (
    !!window.Telegram?.WebApp ||
    !!(window as any).WebApp ||
    navigator.userAgent.includes('MAX') ||
    navigator.userAgent.includes('iPhone') ||
    navigator.userAgent.includes('Mobile') ||
    window.location.pathname.startsWith('/app')
  );

  if (isWebView) {
    return <MemoryRouter initialEntries={initialEntries}>{children}</MemoryRouter>;
  }

  return <BrowserRouter>{children}</BrowserRouter>;
}

export function App(): React.JSX.Element {
  const [tapCount, setTapCount] = React.useState(0);
  const [isAppDisabled, setIsAppDisabled] = React.useState(false);
  const [disabledBotName, setDisabledBotName] = React.useState<string | undefined>();

  React.useEffect(() => {
    const handleDisabled = (e: Event) => {
      const customEvent = e as CustomEvent<{ botName?: string }>;
      setIsAppDisabled(true);
      if (customEvent.detail?.botName) {
        setDisabledBotName(customEvent.detail.botName);
      }
    };

    const handleWindowError = (event: ErrorEvent) => {
      const initData = getPlatformBridge().initData;
      if (initData) {
        logMiniAppEvent(
          initData,
          'error',
          `[Unhandled Error] ${event.message || 'Script error'}`,
          `File: ${event.filename}:${event.lineno}:${event.colno}\nError: ${event.error?.stack || String(event.error)}`
        ).catch(() => {});
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const msg = reason instanceof Error ? reason.message : (typeof reason === 'object' ? JSON.stringify(reason) : String(reason));
      if (!msg || msg.includes('WebAppSetupSwipesBehavior') || msg.includes('request_timeout') || msg === '{}') {
        return;
      }
      const initData = getPlatformBridge().initData;
      if (initData) {
        const stack = reason instanceof Error ? reason.stack : '';
        logMiniAppEvent(
          initData,
          'error',
          `[Unhandled Rejection] ${msg}`,
          stack || 'Unhandled Promise Rejection'
        ).catch(() => {});
      }
    };

    window.addEventListener('miniapp:disabled', handleDisabled);
    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('miniapp:disabled', handleDisabled);
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const handleVersionClick = () => {
    const nextCount = tapCount + 1;
    setTapCount(nextCount);
    if (nextCount >= 5) {
      setTapCount(0);
      try {
        safeLocalStorage.setItem('eruda', 'true');
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/eruda';
        document.head.appendChild(script);
        script.onload = () => {
          const win = window as any;
          if (win.eruda) {
            win.eruda.init();
            alert('🛠️ Консоль отладки Eruda активирована!');
          }
        };
      } catch (err) {
        console.error('Failed to load Eruda:', err);
      }
    }
  };

  if (isAppDisabled) {
    return (
      <MiniAppDisabledScreen 
        botName={disabledBotName} 
        onRetry={() => {
          setIsAppDisabled(false);
          window.location.reload();
        }} 
      />
    );
  }

  return (
    <ErrorBoundary>
      <SafeBrowserRouter>
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route element={<Layout />}>
              <Route index element={<HomeWrapper />} />
              <Route path="index.html" element={<HomeWrapper />} />
              <Route path="bookings" element={<BookingsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="staff" element={<StaffPage />} />
              <Route path="reviews" element={<ReviewsPage />} />
              <Route path="crm" element={<CRMPage />} />
              <Route path="onboarding" element={<OnboardingPage />} />
              <Route path="accept-invite/:code" element={<AcceptInvitePage />} />
              
              {/* B2C Клиентские маршруты */}
              <Route path="client-history" element={<ClientHistoryPage />} />
              <Route path="client-loyalty" element={<ClientLoyaltyPage />} />
              
              {/* Админские маршруты */}
              <Route path="owners" element={<OwnersPage />} />
              <Route path="ai-agents" element={<AdminAIPage />} />
              <Route path="logs" element={<LogsPage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="promo" element={<PromoPage />} />
              <Route path="finance" element={<FinancePage />} />
              <Route path="sandbox" element={<SandboxPage />} />

              {/* Catch-all роут для обработки всех неизвестных или несовпадающих путей */}
              <Route path="*" element={<HomeWrapper />} />
            </Route>
          </Routes>
        </Suspense>
      </SafeBrowserRouter>
      <div 
        onClick={handleVersionClick}
        style={{ 
          position: 'fixed', 
          bottom: '4px', 
          left: '8px', 
          fontSize: '9px', 
          opacity: 0.4, 
          pointerEvents: 'auto', 
          cursor: 'pointer',
          zIndex: 10000, 
          fontFamily: 'system-ui, sans-serif',
          userSelect: 'none'
        }}
      >
        v1.3.0 {tapCount > 0 ? `(${tapCount}/5)` : ''}
      </div>
    </ErrorBoundary>
  );
}

export default App;
